import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchRestaurantDetail } from "../../../Constants/restaurantDetailFetch";
import "./detalleRestaurante.css";
import Loader from "../../Elements/Loader/Loader";
import Button from "../../Elements/Button/Button";
import { getHorarioStatus } from "../../../Utilities/Horarios/horarios";
import useGeolocation from "../../../Hooks/useGeolocation";
import { RADIUS_KM } from "../../../Utilities/geocode";
import { fetchOrdersByRestaurant } from "../../../Services/fetchGetOrdersByRestaurant";
import { useAuthentication } from "../../../Hooks/useAuthentication";

// 🔹 Haversine
const distanceBetweenKm = (a, b) => {
  if (!a || !b) return null;
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const aHarv = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(aHarv), Math.sqrt(1 - aHarv));
  return R * c;
};

const DetalleRestaurante = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuthentication();
  const isAdmin = ["admin", "administrador", "administrator"].includes(
    String(role || "").toLowerCase()
  );

  const restauranteId = useMemo(() => {
    if (id) return id;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("restauranteId");
  }, [id]);

  const [rest, setRest] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [hasPending, setHasPending] = useState(false);
  const [checkingPending, setCheckingPending] = useState(false);

  // 📍 Geolocalización (se requiere para NO dueños; informativa para el dueño)
  const { status: geoStatus, coords } = useGeolocation();

  // Leer usuario desde localStorage (soporta {data:{...}} o plano)
  useEffect(() => {
    const raw = localStorage.getItem("logindata");
    if (raw) {
      try { setUser(JSON.parse(raw)); } catch { setUser(null); }
    }
  }, []);

  // Cargar restaurante
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        const item = await fetchRestaurantDetail(restauranteId);
        setRest(item);
        const init = {};
        (item?.productos || []).forEach((_, idx) => (init[idx] = 0));
        setQuantities(init);
      } catch (e) {
        setError(e.message || "Error cargando restaurante");
      } finally {
        setLoading(false);
      }
    })();
  }, [restauranteId]);

  // --- Normalización de usuario ---
  const normalizedUser = useMemo(() => {
    if (!user) return null;
    return user.data && typeof user.data === "object" ? user.data : user;
  }, [user]);

  const currentUserId = normalizedUser?.userId || null;

  // --- Determinar propietario del restaurante ---
  const ownerId = rest?.createdByUserId || rest?.activatedByUserId || null;
  const isOwner = !!(ownerId && currentUserId && ownerId.trim() === currentUserId.trim());
  // El admin ve el restaurante como si fuera dueño (menú, editar, ver pedidos, reporte semanal)
  const canManage = isOwner || isAdmin;

  // 👉 Si es dueño (o admin), buscamos si hay pedidos pendientes/preparando para mostrar el “dot”
  useEffect(() => {
    if (!canManage || !rest?.restauranteId) return;
    let cancelled = false;
    (async () => {
      setCheckingPending(true);
      try {
        const res = await fetchOrdersByRestaurant(rest.restauranteId);
        const items = Array.isArray(res?.items) ? res.items : [];
        const found = items.some((o) => {
          const s = String(o?.status ?? "").trim().toLowerCase();
          return s === "pendiente" || s === "preparando";
        });
        if (!cancelled) setHasPending(found);
      } catch {
        if (!cancelled) setHasPending(false);
      } finally {
        if (!cancelled) setCheckingPending(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canManage, rest?.restauranteId]);

  // --- Carrito / totales ---
  const setQty = (idx, val) => {
    const n = Math.max(0, Math.min(99, Number(val) || 0));
    setQuantities((q) => ({ ...q, [idx]: n }));
  };

  const cartItems = useMemo(() => {
    if (!rest) return [];
    return (rest.productos || [])
      .map((p, idx) => ({ ...p, qty: quantities[idx] || 0 }))
      .filter((p) => p.qty > 0);
  }, [rest, quantities]);

  const total = cartItems.reduce((acc, it) => acc + it.qty * Number(it.precio || 0), 0);

  // Distancia
  const distanceKm = useMemo(() => {
    if (!rest || !coords) return null;
    const lat = Number(rest.lat);
    const lng = Number(rest.lng);
    if (!isFinite(lat) || !isFinite(lng)) return null;
    return distanceBetweenKm(coords, { lat, lng });
  }, [rest, coords]);

  const insideRadius = typeof distanceKm === "number" && distanceKm <= RADIUS_KM;

  // ----------------- RENDER -----------------
  if (loading) return <Loader />;
  if (error) {
    return (
      <div className="rd-page container-fluid container-mh">
        <p className="error">{error}</p>
      </div>
    );
  }
  if (!rest) return null;

  // ✅ Regla 2: si ES propietario o admin → siempre puede ver (sin exigir ubicación ni radio)
  if (canManage) {
    const status = getHorarioStatus(rest.horarios);
    return (
      <section className="rd-page container-fluid container-mh py-3 py-md-4">
        <div className="row g-3 align-items-start rd-head">
          <div className="col-12 col-lg-8">
            <h2 className="rd-title">{rest.name}</h2>
            <h2 className="chip-info">{rest.phone}</h2>
            <h2 className="chip-muted">{rest.email}</h2>
            {rest.direccion && <p className="muted">{rest.direccion}</p>}

            {(rest.categoriaPrincipal || rest.categorias?.length > 0) && (
              <div className="chip-wrap">
                {rest.categoriaPrincipal && (
                  <span className="chip chip-principal">{rest.categoriaPrincipal}</span>
                )}
                {Array.isArray(rest.categorias) &&
                  rest.categorias.map((c, i) => (
                    <span key={i} className="chip">{c}</span>
                  ))}
              </div>
            )}

            {status && <p className={`status ${status.status}`}>⏰ {status.label}</p>}

            {typeof distanceKm === "number" && (
              <p className="muted">Distancia aproximada: {distanceKm.toFixed(2)} km</p>
            )}
          </div>

          <div className="col-12 col-lg-4">
            <div className="rd-buttons d-flex flex-wrap justify-content-lg-end">
              <Button
                text="Editar restaurante"
                action={() => navigate(`/restaurants/edit-my-restaurant/${rest.restauranteId}`)}
                color="orange"
                fill
              />
              <Button text="Volver" action={() => navigate(-1)} color="blue" fill classNames="link" />
              <div className="btnWrapper">
                <Button
                  text="Ver Pedidos"
                  action={() => navigate(`/restaurants/${rest.restauranteId}/orders`)}
                  color="green"
                  fill
                />
                {(hasPending && !checkingPending) && (
                  <>
                    <span className="dotRing" />
                    <span className="dot" />
                  </>
                )}
                {(checkingPending && !hasPending) && <span className="dotChecking" />}
              </div>
            </div>
          </div>
        </div>

        <h3 className="rd-subtitle mt-3">Menú</h3>

        <div className="row g-3">
          {(rest.productos || []).map((p, idx) => (
            <div className="col-12 col-sm-6 col-lg-4" key={`${p.name}-${idx}`}>
              <div className="rd-card h-100">
                {p.imageUrl && (
                  <div
                    className="rd-img"
                    style={{ backgroundImage: `url(${p.imageUrl})` }}
                    role="img"
                    aria-label={p.name}
                  />
                )}

                <div className="rd-row">
                  <div>
                    <div className="rd-name">{p.name}</div>
                    {p.descripcion && <div className="muted">{p.descripcion}</div>}
                  </div>
                  <div className="rd-price">${Number(p.precio || 0).toFixed(2)}</div>
                </div>

                <div className="rd-actions">
                  <label className="qty-label">
                    Cantidad
                    <input
                      type="number"
                      min="0"
                      max="99"
                      value={quantities[idx] || 0}
                      onChange={(e) => setQty(idx, e.target.value)}
                      disabled={!p.isAvailable}
                    />
                  </label>

                  {p.isAvailable ? (
                    <span className="badge-on">Disponible</span>
                  ) : (
                    <span className="badge-off">No disponible</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rd-footer">
          <div className="container">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div className="rd-total">Total: <strong>${total.toFixed(2)}</strong></div>
              <button
                type="button"
                className={`fill-green border-none BigButton quality-button home-add-btn ${total <= 0 ? "is-disabled" : ""}`}
                aria-disabled={total <= 0}
                onClick={() => {
                  if (total <= 0) return;
                  const orderId = Date.now();
                  const pedido = {
                    orderId,
                    restauranteId: rest.restauranteId,
                    restauranteNombre: rest.name,
                    userId: currentUserId || null,
                    items: cartItems,
                    subtotal: total,
                    envio: Number(rest.costoEnvio) || 0,
                    total: total + (Number(rest.costoEnvio) || 0),
                    status: "CREADO",
                  };
                  localStorage.setItem(`pedido-${orderId}`, JSON.stringify(pedido));
                  window.location.href = `/restaurants/${rest.restauranteId}/create-order/${orderId}`;
                }}
              >
                Hacer pedido
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ⏳ Regla 1 (NO dueño): se exige ubicación
  if (geoStatus === "pending") {
    return (
      <div className="rd-page container-fluid container-mh py-3 py-md-4">
        <h2 className="rd-title">{rest.name}</h2>
        <p className="muted">Solicitando tu ubicación…</p>
      </div>
    );
  }

  if (geoStatus !== "granted") {
    return (
      <div className="rd-page container-fluid container-mh py-3 py-md-4">
        <h2 className="rd-title">{rest.name}</h2>
        <p className="error">Para ver este restaurante necesitas habilitar tu ubicación.</p>
        <Button text="Volver" color="blue" fill action={() => navigate(-1)} />
      </div>
    );
  }

  // ❌ Fuera del radio para NO dueño
  if (!insideRadius) {
    return (
      <div className="rd-page container-fluid container-mh py-3 py-md-4">
        <h2 className="rd-title">{rest.name}</h2>
        <p className="error">
          Este restaurante está a {typeof distanceKm === "number" ? distanceKm.toFixed(2) : "—"} km de ti y
          queda fuera del radio permitido (≤ {RADIUS_KM} km).
        </p>
        <div className="rd-buttons d-flex gap-2">
          <Button text="Volver" color="blue" fill action={() => navigate(-1)} />
          <Button text="Ver cercanos" color="green" fill action={() => navigate("/")} />
        </div>
      </div>
    );
  }

  // ✅ NO dueño dentro del radio → puede ver y hacer pedido
  const status = getHorarioStatus(rest.horarios);

  return (
    <section className="rd-page container-fluid container-mh py-3 py-md-4">
      <div className="row g-3 align-items-start rd-head">
        <div className="col-12 col-lg-8">
          <h2 className="rd-title">{rest.name}</h2>
          <h2 className="chip-info">{rest.phone}</h2>
          <h2 className="chip-muted">{rest.email}</h2>
          {rest.direccion && <p className="muted">{rest.direccion}</p>}

          {(rest.categoriaPrincipal || rest.categorias?.length > 0) && (
            <div className="chip-wrap">
              {rest.categoriaPrincipal && (
                <span className="chip chip-principal">{rest.categoriaPrincipal}</span>
              )}
              {Array.isArray(rest.categorias) &&
                rest.categorias.map((c, i) => (
                  <span key={i} className="chip">{c}</span>
                ))}
            </div>
          )}

          {status && <p className={`status ${status.status}`}>⏰ {status.label}</p>}

          {typeof distanceKm === "number" && (
            <p className="muted">Distancia aproximada: {distanceKm.toFixed(2)} km</p>
          )}
        </div>

        <div className="col-12 col-lg-4">
          <div className="rd-buttons d-flex flex-wrap justify-content-lg-end">
            {/* Solo el dueño puede editar/ver pedidos */}
            <Button text="Volver" action={() => navigate(-1)} color="blue" fill classNames="link" />
          </div>
        </div>
      </div>

      <h3 className="rd-subtitle mt-3">Menú</h3>

      <div className="row g-3">
        {(rest.productos || []).map((p, idx) => (
          <div className="col-12 col-sm-6 col-lg-4" key={`${p.name}-${idx}`}>
            <div className="rd-card h-100">
              {p.imageUrl && (
                <div
                  className="rd-img"
                  style={{ backgroundImage: `url(${p.imageUrl})` }}
                  role="img"
                  aria-label={p.name}
                />
              )}

              <div className="rd-row">
                <div>
                  <div className="rd-name">{p.name}</div>
                  {p.descripcion && <div className="muted">{p.descripcion}</div>}
                </div>
                <div className="rd-price">${Number(p.precio || 0).toFixed(2)}</div>
              </div>

              <div className="rd-actions">
                <label className="qty-label">
                  Cantidad
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={quantities[idx] || 0}
                    onChange={(e) => setQty(idx, e.target.value)}
                    disabled={!p.isAvailable}
                  />
                </label>

                {p.isAvailable ? (
                  <span className="badge-on">Disponible</span>
                ) : (
                  <span className="badge-off">No disponible</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rd-footer">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <div className="rd-total">Total: <strong>${total.toFixed(2)}</strong></div>
            <button
              type="button"
              className={`fill-green border-none BigButton quality-button home-add-btn ${total <= 0 ? "is-disabled" : ""}`}
              aria-disabled={total <= 0}
              onClick={() => {
                if (total <= 0) return;
                const orderId = Date.now();
                const pedido = {
                  orderId,
                  restauranteId: rest.restauranteId,
                  restauranteNombre: rest.name,
                  userId: currentUserId || null,
                  items: cartItems,
                  subtotal: total,
                  envio: Number(rest.costoEnvio) || 0,
                  total: total + (Number(rest.costoEnvio) || 0),
                  status: "CREADO",
                };
                localStorage.setItem(`pedido-${orderId}`, JSON.stringify(pedido));
                window.location.href = `/restaurants/${rest.restauranteId}/create-order/${orderId}`;
              }}
            >
              Hacer pedido
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DetalleRestaurante;